/**
 * The boot plate: the STILLSUN mark, and one passage of lore typing itself out.
 *
 * Owner brief (2026-09-04): "instead of a loading bar on that first page, make it part of a
 * random part of the lore that writes out until it finishes and then it loads to the enter
 * screen."
 *
 * So there is no bar and no percentage any more. The markup is static in `index.html` so the
 * plate is up before a single module has been evaluated; this class only drives it.
 *
 * ## The pace
 * The typing runs at a steady reading cadence (`CPS`) with the rhythm a person has: a beat after
 * a comma, a longer one after a full stop, a breath at a paragraph. It is deliberately NOT tied
 * to load progress. The first version re-estimated the rate from the loader every tick, and the
 * loader moves in jumps, so the text sprinted and stalled with it (Chris, 2026-09-05: "reads
 * like a broken typewriter"). Now the loader only decides when the plate may leave:
 *
 * - load finished first: the passage keeps typing at its own pace to the last word, then fades;
 * - passage finished first: the last line is held with the cursor breathing until the load lands.
 *
 * A main-thread stall (shader compile) cannot burst the text either: elapsed time per tick is
 * capped, so after a freeze the typing simply resumes at cadence. Any click or key skips to the
 * end at `SKIP_CPS` (fast, not instant). A second passage is never started.
 */

import { randomLore, type LorePassage } from './lore.js';

export type SplashStage = 'renderer' | 'scene' | 'shaders' | 'post' | 'ready';

const TICK_MS = 40;
/** Steady reading cadence, characters per second. ~40 is a brisk human typist and a comfortable read. */
const CPS = 40;
/** The most wall-clock time one tick may account for: a frozen main thread never dumps text. */
const MAX_TICK_MS = 90;
/** Beats after punctuation, ms. */
const PAUSE_STOP_MS = 260; // . ! ? …
const PAUSE_CLAUSE_MS = 110; // , ; : —
const PAUSE_BREAK_MS = 340; // paragraph break
/** Skipping types the rest out fast rather than instantly: an instant fill loses the gesture. */
const SKIP_CPS = 320;
/** How long the finished plate takes to fade to the callsign card. */
const FADE_MS = 400;

function need<T extends HTMLElement>(id: string): T | null {
  return document.getElementById(id) as T | null;
}

export class Splash {
  private readonly root = need<HTMLDivElement>('splash');
  private readonly loreEl = need<HTMLDivElement>('splash-lore');
  private readonly titleEl = need<HTMLSpanElement>('splash-lore-title');
  private readonly typedEl = need<HTMLSpanElement>('splash-typed');
  private readonly speakerEl = need<HTMLSpanElement>('splash-lore-speaker');

  /** The passage on screen, so the deploy cinematic can ask for a different one. */
  readonly passage: LorePassage;

  private loadDone = false;
  /** Wall clock of the last tick and the moment a punctuation beat ends. */
  private lastTickAt = performance.now();
  private pauseUntil = 0;
  /** Characters actually written. Fractional between ticks so slow rates are not quantised away. */
  private typed = 0;
  private skipping = false;
  private closed = false;
  private failed = false;
  private timer: ReturnType<typeof setInterval> | null = null;
  private readonly onSkip: (ev: Event) => void;

  constructor(exclude?: string) {
    this.passage = randomLore(exclude);
    const root = this.root;
    if (root) {
      root.classList.remove('idle', 'done', 'failed');
      if (this.titleEl) this.titleEl.textContent = this.passage.title;
      if (this.speakerEl) this.speakerEl.textContent = `— ${this.passage.speaker}`;
      if (this.typedEl) this.typedEl.textContent = '';
      this.loreEl?.classList.add('typing');
      this.loreEl?.classList.remove('held', 'done');
    }

    // "Any click/key skips the typing to the end but never past the load." Registered on the
    // window rather than the plate so it works wherever the pointer happens to be, and removed
    // the moment the plate closes so it can never eat a keystroke meant for the callsign field.
    this.onSkip = () => {
      this.skipping = true;
    };
    window.addEventListener('pointerdown', this.onSkip, { passive: true });
    window.addEventListener('keydown', this.onSkip);

    this.timer = setInterval(() => {
      this.tick();
    }, TICK_MS);
  }

  /** A boot stage has begun. Kept for the boot sequence's bookkeeping; the typing ignores it. */
  stage(stage: SplashStage): void {
    if (this.closed) return;
    if (stage === 'ready') this.loadDone = true;
  }

  /** Everything is compiled. The plate stays up until the passage has finished its last word. */
  done(): void {
    if (this.closed) return;
    this.stage('ready');
    this.loadDone = true;
  }

  /**
   * Boot failed: stop typing, say why in place of the passage, and leave it on screen. The start
   * card underneath carries the same reason on its own boot line.
   */
  fail(message: string): void {
    if (this.closed) return;
    this.failed = true;
    this.loadDone = true;
    if (this.titleEl) this.titleEl.textContent = 'Boot failed';
    if (this.typedEl) this.typedEl.textContent = message;
    if (this.speakerEl) this.speakerEl.textContent = '';
    this.loreEl?.classList.remove('typing', 'held');
    this.loreEl?.classList.add('done');
    this.root?.classList.add('failed');
    // Held a beat so the message is readable, then out of the way of the start card.
    setTimeout(() => {
      this.close();
    }, 2600);
  }

  private get total(): number {
    return this.passage.text.length;
  }

  private tick(): void {
    if (this.closed || this.failed) return;

    // 1. Time since the last tick, capped: a shader-compile freeze resumes at cadence, it does
    //    not pour the missed seconds out as a burst.
    const now = performance.now();
    const dt = Math.min(MAX_TICK_MS, Math.max(0, now - this.lastTickAt));
    this.lastTickAt = now;

    // 2. Type. Steady cadence, with a beat after punctuation; skipping is fast but still typed.
    const before = Math.floor(this.typed);
    if (this.skipping) {
      this.typed = Math.min(this.total, this.typed + (SKIP_CPS * dt) / 1000);
    } else if (now >= this.pauseUntil) {
      this.typed = Math.min(this.total, this.typed + (CPS * dt) / 1000);
    }
    const chars = Math.floor(this.typed);
    if (chars > before && !this.skipping) {
      // Only the newest character can start a beat, and only one beat per character.
      const last = this.passage.text.charAt(chars - 1);
      if (last === '\n') this.pauseUntil = now + PAUSE_BREAK_MS;
      else if ('.!?…'.includes(last)) this.pauseUntil = now + PAUSE_STOP_MS;
      else if (',;:—'.includes(last)) this.pauseUntil = now + PAUSE_CLAUSE_MS;
      // A beat lands on the whole character: snap the fraction so the pause is not eaten.
      this.typed = chars;
    }

    // 3. Write only when a whole character has arrived — `textContent` is the expensive part.
    if (this.typedEl && this.typedEl.textContent?.length !== chars) {
      this.typedEl.textContent = this.passage.text.slice(0, chars);
    }

    // 4. States: typing, held (text finished, load has not), done (both).
    const textDone = chars >= this.total;
    if (textDone && !this.loadDone) {
      this.loreEl?.classList.remove('typing');
      this.loreEl?.classList.add('held');
    } else if (!textDone) {
      this.loreEl?.classList.add('typing');
      this.loreEl?.classList.remove('held');
    }

    if (textDone && this.loadDone) {
      this.loreEl?.classList.remove('typing', 'held');
      this.loreEl?.classList.add('done');
      this.close();
    }
  }

  /**
   * Fade out and stop — but leave the element in the document. It used to `remove()` itself,
   * which was right when this was only ever the boot; the deploy cinematic is a separate plate
   * now, but a failed boot still wants this one to stay addressable.
   */
  private close(): void {
    if (this.closed) return;
    this.closed = true;
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    window.removeEventListener('pointerdown', this.onSkip);
    window.removeEventListener('keydown', this.onSkip);
    this.root?.classList.add('done');
    setTimeout(() => {
      this.root?.remove();
    }, FADE_MS + 200);
  }
}
